import {all, call, delay, put, putResolve, select, takeLatest} from 'redux-saga/effects';
import {Action} from '@reduxjs/toolkit';
import {
  createUser,
  deleteUserRole,
  getFilteredUsersList,
  getManageTechContentUsersList,
  getManageUsersList,
  getUserDetails,
  getUserForEditWithRole,
  loadSystemAccessRequest,
  requestSystemAccess,
  resetRoleAssignmentsData,
  roleAssignmentSetIsDirty,
  roleAssignmentSetSaveOperation,
  saveNewUserRoleAssignments,
  setAddUserModalData,
  setAddUserSearchText,
  setCurrentSystemAccessRequest,
  setSystemAccessRequestReason,
  setEditUserData,
  setManageTechContentUsersData,
  setManageUsersData,
  setRoleAssignments,
  setRoleAssignmentUsers,
  setSearchLDAPManageTechContentUsersText,
  setSearchLDAPManageUsersText,
  setSearchManageTechContentUsersText,
  setSearchManageUsersText,
  setSystemAccessLoadOperation,
  setUserDetails,
  submitSystemAccessRequestApproval,
  toggleRole,
  updateUser,
} from './userManagementActions';
import {RootState} from "../rootStore";
import {
  IAddUserModalData,
  IEditUserData,
  IManageUsersData,
  IUserDetail,
  IUserFilterFields,
  IUserManagementState
} from './userManagementTypes';
import UserApi from 'apis/user/UserApi';
import {
  IAADUser,
  IGetSystemAccessRequestResponse,
  IManageUserUser,
  Roles,
  SystemAccessApprovalResponseTypes
} from 'interfaces/user/UserInterfaces';
import {getResponseErrorMessage} from 'utilities/validationErrorHelpers';
import {showErrorToast, showSuccessToast} from 'store/toast/toastActions';
import {Routes} from 'components/routing/Routing';
import {cloneDeep} from 'lodash';
import SystemAccessRequestApi from "../../apis/systemAccessRequests/SystemAccessRequestApi";

export default function* watchUserManagementSagas() {
  yield all([
    watchSetAddUserSearchText(),
    watchGetManageUsersList(),
    watchGetLDAPManageUsersList(),
    watchGetTechContentManageUsersList(),
    watchGetLDAPTechContentManageUsersList(),
    watchGetUserForEdit(),
    watchUpdateUser(),
    watchCreateUser(),
    watchGetUserDetails(),
    watchDeleteUserRole(),
    watchGetFilteredUserList(),
    watchSaveNewUserRoleAssignments(),
    watchRequestSystemAccess(),
    watchLoadSystemAccessRequest(),
    watchSubmitSystemAccessRequestApproval(),
  ]);
}

function* watchSetAddUserSearchText() {
  yield takeLatest(setAddUserSearchText, setAddUserSearchTextAsync);
}

function* watchGetManageUsersList() {
  yield takeLatest([
    getManageUsersList,
    setSearchManageUsersText,
  ], refreshManageUsersListAsync);
}

function* watchGetLDAPManageUsersList() {
  yield takeLatest(setSearchLDAPManageUsersText, refreshLDAPManageUsersListAsync);
}

function* watchGetTechContentManageUsersList() {
  yield takeLatest([
    getManageTechContentUsersList,
    setSearchManageTechContentUsersText,
  ], refreshManageTechContentUsersListAsync);
}

function* watchGetLDAPTechContentManageUsersList() {
  yield takeLatest(setSearchLDAPManageTechContentUsersText, refreshLDAPManageTechContentUsersListAsync);
}

function* watchGetUserForEdit() {
  yield takeLatest([getUserForEditWithRole, toggleRole], getUserForEditAsync);
}

function* watchDeleteUserRole() {
  yield takeLatest(deleteUserRole, deleteUserRoleAsync);
}

function* watchGetUserDetails() {
  yield takeLatest(getUserDetails, getUserDetailsAsync)
}

function* watchCreateUser() {
  yield takeLatest(createUser, createUserAsync);
}

function* watchUpdateUser() {
  yield takeLatest(updateUser, updateUserAsync);
}

function* watchGetFilteredUserList() {
  yield takeLatest(getFilteredUsersList, getFilteredUsersListAsync);
}

function* watchSaveNewUserRoleAssignments() {
  yield takeLatest(saveNewUserRoleAssignments, saveNewUserRoleAssignmentsAsync);
}

function* watchRequestSystemAccess() {
  yield takeLatest(requestSystemAccess, requestSystemAccessAsync);
}

function* watchLoadSystemAccessRequest() {
  yield takeLatest(loadSystemAccessRequest, loadSystemAccessRequestAsync);
}

function* watchSubmitSystemAccessRequestApproval() {
  yield takeLatest(submitSystemAccessRequestApproval, submitSystemAccessRequestApprovalAsync);
}

function* setAddUserSearchTextAsync(action: Action) {
  if (!setAddUserSearchText.match(action)) {
    return;
  }

  let modalData: IAddUserModalData =
    yield select((store: RootState) => store.userManagement.addUserModalData);

  // If modal is closed, quit.
  if (!modalData.isOpen) {
    return;
  }

  // If no search text, clear modal results.
  if (!modalData.searchText.trim()) {
    yield put(setAddUserModalData({
      ...modalData,
      searchResults: [],
      loadOperation: undefined,
    }));
    return;
  }

  yield put(setAddUserModalData({
    ...modalData,
    loadOperation: {
      isWorking: true,
      errorMessage: "",
      wasSuccessful: false,
    },
  }));

  try {
    const searchResults: IAADUser[] = yield call(UserApi.searchAADUsers, action.payload);

    yield put(setAddUserModalData({
      ...modalData,
      searchResults,
      loadOperation: undefined,
    }));
  } catch (err: any) {
    yield put(setAddUserModalData({
      ...modalData,
      loadOperation: {
        isWorking: false,
        wasSuccessful: false,
        errorMessage: getResponseErrorMessage(err),
      },
    }));
  }
}

function* refreshLDAPManageUsersListAsync(action: Action) {
  let searchText = "";

  if (setSearchLDAPManageUsersText.match(action)) {
    searchText = action.payload;
  }

  const manageUsersData: IManageUsersData = yield select((store: RootState) => store
    .userManagement
    .manageUsersData);

  if (getManageUsersList.match(action)) {
    searchText = manageUsersData.searchText;
  }

  yield put(setManageUsersData({
    ...manageUsersData,
    getUsersOperation: {
      isWorking: true,
      errorMessage: "",
      wasSuccessful: false,
    },
  }));

  let ldapUserList: IAADUser[] = [];

  try {
    ldapUserList = yield call(UserApi.searchAADUsers, searchText);
  } catch (err: any) {
    yield put(setManageUsersData({
      ...manageUsersData,
      getUsersOperation: {
        isWorking: false,
        errorMessage: getResponseErrorMessage(err),
        wasSuccessful: false,
      },
    }));
    return;
  }

  yield put(setManageUsersData({
    ...manageUsersData,
    ldapUserList,
    getUsersOperation: undefined,
  }));
}

function* refreshManageUsersListAsync(action: Action) {
  let searchText = "";

  if (setSearchManageUsersText.match(action)) {
    searchText = action.payload;
  }

  const manageUsersData: IManageUsersData = yield select((store: RootState) => store
    .userManagement
    .manageUsersData);

  if (getManageUsersList.match(action)) {
    searchText = manageUsersData.searchText;
  }

  yield put(setManageUsersData({
    ...manageUsersData,
    getUsersOperation: {
      isWorking: true,
      errorMessage: "",
      wasSuccessful: false,
    },
  }));

  let userList: IManageUserUser[] = [];

  try {
    userList = yield call(UserApi.getManageUsersList, searchText);
  } catch (err: any) {
    yield put(setManageUsersData({
      ...manageUsersData,
      getUsersOperation: {
        isWorking: false,
        errorMessage: getResponseErrorMessage(err),
        wasSuccessful: false,
      },
    }));
    return;
  }

  yield put(setManageUsersData({
    ...manageUsersData,
    userList,
    getUsersOperation: undefined,
  }));
}

function* refreshLDAPManageTechContentUsersListAsync(action: Action) {
  let searchText = "";

  if (setSearchLDAPManageTechContentUsersText.match(action)) {
    searchText = action.payload;
  }

  const manageTechContentUsersData: IManageUsersData = yield select((store: RootState) => store
    .userManagement
    .manageTechContentUsersData);

  if (getManageTechContentUsersList.match(action)) {
    searchText = manageTechContentUsersData.searchText;
  }

  yield put(setManageTechContentUsersData({
    ...manageTechContentUsersData,
    getUsersOperation: {
      isWorking: true,
      errorMessage: "",
      wasSuccessful: false,
    },
  }));

  let ldapUserList: IAADUser[] = [];

  try {
    ldapUserList = yield call(UserApi.searchAADUsers, searchText);
  } catch (err: any) {
    yield put(setManageTechContentUsersData({
      ...manageTechContentUsersData,
      getUsersOperation: {
        isWorking: false,
        errorMessage: getResponseErrorMessage(err),
        wasSuccessful: false,
      },
    }));
    return;
  }

  yield put(setManageTechContentUsersData({
    ...manageTechContentUsersData,
    ldapUserList,
    getUsersOperation: undefined,
  }));
}

function* refreshManageTechContentUsersListAsync(action: Action) {
  let searchText = "";

  if (setSearchManageTechContentUsersText.match(action)) {
    searchText = action.payload;
  }

  const manageTechContentUsersData: IManageUsersData = yield select((store: RootState) => store
    .userManagement
    .manageTechContentUsersData);

  if (getManageTechContentUsersList.match(action)) {
    searchText = manageTechContentUsersData.searchText;
  }

  yield put(setManageTechContentUsersData({
    ...manageTechContentUsersData,
    getUsersOperation: {
      isWorking: true,
      errorMessage: "",
      wasSuccessful: false,
    },
  }));

  let userList: IManageUserUser[] = [];

  try {
    userList = yield call(UserApi.getManageUsersList, searchText);
  } catch (err: any) {
    yield put(setManageTechContentUsersData({
      ...manageTechContentUsersData,
      getUsersOperation: {
        isWorking: false,
        errorMessage: getResponseErrorMessage(err),
        wasSuccessful: false,
      },
    }));
    return;
  }

  yield put(setManageTechContentUsersData({
    ...manageTechContentUsersData,
    userList,
    getUsersOperation: undefined,
  }));
}

function* deleteUserRoleAsync(action: Action) {
  if (!deleteUserRole.match(action)) {
    return;
  }

  yield put(setUserDetails({
    email: "",
    name: "",
    getUserDetailOperation: {
      isWorking: true,
      wasSuccessful: false,
      errorMessage: "",
    }
  }));

  const userDet: IUserDetail = yield select((store: RootState) => store
    .userManagement
    .userDetail);
  let user: IUserDetail | null;

  try {
    user = yield call(UserApi.deleteUserRole,
      action.payload.email,
      action.payload.role,
      action.payload.isTechUser,
      action.payload.isPsdUser,
      action.payload.isMfgUser);
  } catch (err: any) {
    yield put(setUserDetails({
      ...userDet,
      getUserDetailOperation: {
        isWorking: false,
        wasSuccessful: false,
        errorMessage: getResponseErrorMessage(err),
      },
    }));
    return;
  }

  if (user) {
    yield put(setUserDetails({
      email: user.email,
      name: user.name,
      userDetails: user.userDetails,

      getUserDetailOperation: undefined,
    }));

    yield put(showSuccessToast(`Role deleted successfully.`));
  } else {
    yield put(setUserDetails({
      ...userDet,
      getUserDetailOperation: {
        isWorking: false,
        wasSuccessful: false,
        errorMessage: `The user ${action.payload} was not found.`,
      },
    }));
  }
}

function* getUserDetailsAsync(action: Action) {
  if (!getUserDetails.match(action)) {
    return;
  }

  yield put(setUserDetails({
    email: "",
    name: "",
    getUserDetailOperation: {
      isWorking: true,
      wasSuccessful: false,
      errorMessage: "",
    }
  }));

  const userDet: IUserDetail = yield select((store: RootState) => store
    .userManagement
    .userDetail);
  let user: IUserDetail | null;

  try {
    user = yield call(UserApi.getUserDetail, action.payload.email);

    if (user) {
      yield put(setUserDetails({
        email: user.email,
        name: user.name,
        userDetails: user.userDetails,
        getUserDetailOperation: {
          isWorking: false,
          wasSuccessful: true,
          errorMessage: "",
        }
      }));
    } else {
      yield put(setUserDetails({
        ...userDet,
        getUserDetailOperation: {
          isWorking: false,
          wasSuccessful: false,
          errorMessage: `The user ${action.payload} was not found.`,
        },
      }));
    }

  } catch (err: any) {
    yield put(setUserDetails({
      ...userDet,
      getUserDetailOperation: {
        isWorking: false,
        wasSuccessful: false,
        errorMessage: getResponseErrorMessage(err),
      },
    }));
    return;
  }

}

function* getUserForEditAsync(action: Action) {
  if (!getUserForEditWithRole.match(action)
    && !toggleRole.match(action)) {
    return;
  }

  const editUserData: IEditUserData = yield select((store: RootState) => store
    .userManagement
    .editUserData);

  yield put(setEditUserData({
    ...editUserData,
    loadOperation: {
      isWorking: true,
      errorMessage: "",
      wasSuccessful: false,
    },
  }));

  let user: IEditUserData | null;

  try {

    user = yield call(UserApi.getUserEditInfo,
      action.payload.email,
      action.payload.role,
      action.payload.isTechUser,
      action.payload.isPsdUser,
      action.payload.isMfgUser);

  } catch (err: any) {
    yield put(setEditUserData({
      ...editUserData,
      loadOperation: {
        isWorking: false,
        wasSuccessful: false,
        errorMessage: getResponseErrorMessage(err),
      },
    }));
    return;
  }

  if (user) {
    let enableisDirty = false;

    enableisDirty = user.user?.role === Roles.ApproverCOE
      || user.user?.role === Roles.SysAdmin
      || user.user?.role === Roles.TechComm;

    yield put(setEditUserData({
      ...user,
      originalUser: cloneDeep(user.user),
      isDirty: enableisDirty,
      loadOperation: undefined,
    }));
  } else {
    yield put(setEditUserData({
      ...editUserData,
      loadOperation: {
        isWorking: false,
        wasSuccessful: false,
        errorMessage: `The user ${action.payload} was not found.`,
      },
    }));
  }
}

function* createUserAsync(action: Action) {
  if (!createUser.match(action)) {
    return;
  }

  const {
    addUserModalData,
  }: IUserManagementState = yield select((store: RootState) => store.userManagement);

  yield put(setAddUserModalData({
    ...addUserModalData,
    createOperation: {
      isWorking: true,
    },
  }));

  try {
    yield call(UserApi.createUser, action.payload.user);
  } catch (err: any) {
    yield put(setAddUserModalData({
      ...addUserModalData,
      createOperation: undefined,
    }));
    yield put(showErrorToast(`Failed to add ${action.payload.user.email}. ${getResponseErrorMessage(err)}`));
    return;
  }

  yield put(setAddUserModalData({
    ...addUserModalData,
    createOperation: undefined,
    addedUsers: [...addUserModalData.addedUsers, action.payload.user]
  }));

  yield put(showSuccessToast(`${action.payload.user.email} was added successfully.`));
}

function* updateUserAsync(action: Action) {
  if (!updateUser.match(action)) {
    return;
  }

  const editUserData: IEditUserData = yield select((store: RootState) => store
    .userManagement
    .editUserData);

  yield put(setEditUserData({
    ...editUserData,
    saveOperation: {
      isWorking: true,
    },
  }));

  try {
    yield call(UserApi.updateUser, action.payload.user, action.payload.email, action.payload.name);
  } catch (err: any) {
    yield put(setEditUserData({
      ...editUserData,
      saveOperation: undefined,
    }));
    yield put(showErrorToast(`Failed to update ${action.payload.email}. ${getResponseErrorMessage(err)}`));
    return;
  }

  yield put(setEditUserData({
    ...editUserData,
    originalUser: cloneDeep(action.payload.user),
    isDirty: false,
    saveOperation: undefined,
  }));

  yield put(showSuccessToast(`${action.payload.email} was updated successfully.`));
}

function* getFilteredUsersListAsync(action: Action) {
  if (!getFilteredUsersList.match(action)) {
    return;
  }

  const manageTechContentUsersData: IManageUsersData = yield select((store: RootState) => store
    .userManagement
    .manageTechContentUsersData);
  const manageUsersData: IManageUsersData = yield select((store: RootState) => store
    .userManagement
    .manageUsersData);
  const userFilteredFields: IUserFilterFields = yield select((store: RootState) => store
    .userManagement
    .filterData);

  if (action.payload.isListUserSearch) {
    yield put(setManageUsersData({
      ...manageUsersData,
      getUsersOperation: {
        isWorking: true,
        errorMessage: "",
        wasSuccessful: false,
      },
    }));
  } else {
    yield put(setManageTechContentUsersData({
      ...manageTechContentUsersData,
      getUsersOperation: {
        isWorking: true,
        errorMessage: "",
        wasSuccessful: false,
      },
    }));
  }

  let userList: IManageUserUser[] = [];

  let filtersLength = userFilteredFields.owningPlantsFilter.length +
    userFilteredFields.userRolesFilter.length +
    userFilteredFields.owningOrgsFilter.length +
    userFilteredFields.serviceTypesFilter.length +
    userFilteredFields.equipmentsFilter.length +
    userFilteredFields.epicEquipmentsFilter.length +
    userFilteredFields.countriesFilter.length +
    userFilteredFields.geoUnitsFilter.length +
    userFilteredFields.productCentersFilter.length;
  try {
    if (action.payload.isListUserSearch &&
      filtersLength === 0 &&
      action.payload.searchTerm === "") {
      userList = [];
    }
    else {
      userList = yield call(UserApi.getFilteredUsersList, action.payload.searchTerm, userFilteredFields);
    }
  } catch (err: any) {
    if (action.payload.isListUserSearch) {
      yield put(setManageUsersData({
        ...manageUsersData,
        getUsersOperation: {
          isWorking: false,
          errorMessage: getResponseErrorMessage(err),
          wasSuccessful: false,
        },
      }));
    } else {
      yield put(setManageTechContentUsersData({
        ...manageTechContentUsersData,
        getUsersOperation: {
          isWorking: false,
          errorMessage: getResponseErrorMessage(err),
          wasSuccessful: false,
        },
      }));
    }
    return;
  }

  if (action.payload.isListUserSearch) {
    yield put(setManageUsersData({
      ...manageUsersData,
      userList,
      getUsersOperation: undefined,
    }));
  } else {
    yield put(setManageTechContentUsersData({
      ...manageTechContentUsersData,
      userList,
      getUsersOperation: undefined,
    }));
  }
}

function* saveNewUserRoleAssignmentsAsync(action: Action) {
  if (!saveNewUserRoleAssignments.match(action)) {
    return;
  }

  yield put(roleAssignmentSetSaveOperation({
    isWorking: true,
  }));

  try {
    yield call(UserApi.saveNewUserRoleAssignments, action.payload.users, action.payload.roleAssignments);
  } catch (err: any) {
    yield put(roleAssignmentSetSaveOperation(undefined));
    yield put(showErrorToast(`Failed to save changes. ${getResponseErrorMessage(err)}`));
    return;
  }

  yield put(showSuccessToast(`Role assignments were saved successfully.`));
  yield put(resetRoleAssignmentsData());
  yield put(roleAssignmentSetSaveOperation(undefined));
  yield putResolve(roleAssignmentSetIsDirty(false));
  yield delay(500);
  action.payload.navigate(Routes.UserManagement);
}

function* requestSystemAccessAsync(action: Action) {
  if (!requestSystemAccess.match(action)) {
    return;
  }

  yield put(roleAssignmentSetSaveOperation({
    isWorking: true,
  }));

  try {
    yield call(SystemAccessRequestApi.submitSystemAccessRequest, action.payload.roleAssignments, action.payload.requestReason);
  } catch (err: any) {
    yield put(roleAssignmentSetSaveOperation(undefined));
    yield put(showErrorToast(`${getResponseErrorMessage(err)}`));
    return;
  }

  yield put(showSuccessToast(`System access request submitted successfully.`));
  yield put(resetRoleAssignmentsData());
  yield put(setSystemAccessRequestReason(undefined));
  yield delay(500);
  action.payload.navigate(Routes.RequestSystemAccess);

  // Refresh the requesting user's "my request" so the RequestAccess view shows the new status
  try {
    const myRequest: IGetSystemAccessRequestResponse = yield call(SystemAccessRequestApi.getMySystemAccessRequest);
    if (myRequest && myRequest.id) {
      yield put(setCurrentSystemAccessRequest(myRequest.id));
      yield put(setSystemAccessRequestReason(myRequest.requestReason));
      yield put(setRoleAssignments(myRequest.requestDetail));
      yield put(setRoleAssignmentUsers([{ email: myRequest.email, name: myRequest.name }]));
      yield put(roleAssignmentSetSaveOperation(undefined));
    }
  } catch (err: any) {
    // If the refresh fails, surface a non-blocking error so the user still sees the success toast
    yield put(showErrorToast(getResponseErrorMessage(err)));
  }
}

function* loadSystemAccessRequestAsync(action: Action) {
  if (!loadSystemAccessRequest.match(action)) {
    return;
  }

  yield put(setSystemAccessLoadOperation({
    isWorking: true,
  }));

  try {
    let data: IGetSystemAccessRequestResponse = yield call(
      SystemAccessRequestApi.getSystemAccessRequest,
      action.payload);
    yield put(resetRoleAssignmentsData())
    yield put(setCurrentSystemAccessRequest(data.id));
    yield put(setSystemAccessRequestReason(data.requestReason));
    yield put(setRoleAssignments(data.requestDetail));
    yield put(setRoleAssignmentUsers([ { email: data.email, name: data.name }]))
    yield put(roleAssignmentSetSaveOperation(undefined));
  } catch (err: any) {
    yield put(showErrorToast(getResponseErrorMessage(err)));
    yield put(resetRoleAssignmentsData());
    yield put(setSystemAccessLoadOperation({
      isWorking: false,
      wasSuccessful: false,
      errorMessage: getResponseErrorMessage(err),
    }));
  }
}

function* submitSystemAccessRequestApprovalAsync(action: Action) {
  if (!submitSystemAccessRequestApproval.match(action)) {
    return;
  }

  yield put(roleAssignmentSetSaveOperation({
    isWorking: true,
  }));

  try {
    yield call(SystemAccessRequestApi.submitSystemAccessRequestApproval, action.payload.approvalData);
  } catch (err: any) {
    yield put(roleAssignmentSetSaveOperation(undefined));
    yield put(showErrorToast(`${getResponseErrorMessage(err)}`));
    return;
  }
  if(action.payload.approvalData.approvalResponse == SystemAccessApprovalResponseTypes.Approved) {
    yield put(showSuccessToast(`System access request has been Approved.`));
  }
  else{
    yield put(showSuccessToast(`System access request has been Rejected.`));
  }
  yield delay(500);
  action.payload.navigate(Routes.UserManagement);
  yield put(resetRoleAssignmentsData());
}